(() => {
  const root = document.documentElement;
  const liveRegion = document.getElementById("LiveRegion");
  const announce = (message) => {
    if (liveRegion) liveRegion.textContent = message;
  };

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector("input");
      this.addEventListener("click", (event) => {
        const button = event.target.closest("button");
        if (!button || !this.input) return;
        const old = Number(this.input.value || 1);
        button.name === "plus" ? this.input.stepUp() : this.input.stepDown();
        if (Number(this.input.value) !== old) {
          this.input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });
    }
  }
  customElements.define("quantity-input", QuantityInput);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("form");
      this.button = this.querySelector('[name="add"]');
      if (!this.form) return;
      this.form.addEventListener("submit", (event) => this.submit(event));
    }

    async submit(event) {
      if (
        !root.classList.contains("js") ||
        !document.getElementById("CartDrawer")
      )
        return;
      event.preventDefault();
      const errorBox = this.querySelector("[data-product-error]");
      if (errorBox) {
        errorBox.hidden = true;
        errorBox.textContent = "";
      }
      if (this.button) {
        this.button.disabled = true;
        this.button.setAttribute("aria-disabled", "true");
      }
      try {
        const response = await fetch(
          `${window.Shopify?.routes?.root || "/"}cart/add.js`,
          {
            method: "POST",
            headers: { Accept: "application/json" },
            body: new FormData(this.form),
          },
        );
        const item = await response.json();
        if (!response.ok)
          throw new Error(item.description || "Unable to add item");
        await refreshCartDrawer();
        const drawer = document.getElementById("CartDrawer");
        if (drawer?.showModal) {
          drawer.dataset.opener = "CartButton";
          drawer.showModal();
          document
            .getElementById("CartButton")
            ?.setAttribute("aria-expanded", "true");
        }
        announce(`${item.product_title} added to cart.`);
      } catch (error) {
        announce(error.message);
        if (errorBox) {
          errorBox.textContent = error.message;
          errorBox.hidden = false;
        }
      } finally {
        if (this.button) {
          this.button.disabled = false;
          this.button.removeAttribute("aria-disabled");
        }
      }
    }
  }
  customElements.define("product-form", ProductForm);

  async function refreshCartDrawer() {
    const response = await fetch(
      `${window.Shopify?.routes?.root || "/"}?sections=cart-drawer,header`,
    );
    if (!response.ok) return;
    const sections = await response.json();
    const parsed = new DOMParser();
    if (sections["cart-drawer"]) {
      const fresh = parsed
        .parseFromString(sections["cart-drawer"], "text/html")
        .querySelector("[data-cart-drawer-content]");
      const current = document.querySelector("[data-cart-drawer-content]");
      if (fresh && current) current.replaceWith(fresh);
    }
    if (sections.header) {
      const freshCount = parsed
        .parseFromString(sections.header, "text/html")
        .querySelector("[data-cart-count]");
      const currentCount = document.querySelector("[data-cart-count]");
      if (freshCount && currentCount) currentCount.replaceWith(freshCount);
    }
  }

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(
        this.querySelector("[data-variants-json]")?.textContent || "[]",
      );
      this.formId = this.dataset.formId;
      this.addEventListener("change", () => this.update());
    }

    update() {
      const options = [...this.querySelectorAll("fieldset")].map(
        (group) => group.querySelector("input:checked")?.value,
      );
      const variant = this.variants.find((item) =>
        item.options.every((value, index) => value === options[index]),
      );
      const form = document.getElementById(this.formId);
      const idInput = form?.querySelector('[name="id"]');
      const button = form?.querySelector('[name="add"]');
      if (!variant) {
        if (button) {
          button.disabled = true;
          button.setAttribute("aria-disabled", "true");
          button.textContent = button.dataset.unavailable;
        }
        this.updateSticky(null);
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (button) {
        button.disabled = !variant.available;
        button.setAttribute(
          "aria-disabled",
          variant.available ? "false" : "true",
        );
        button.textContent = variant.available
          ? button.dataset.add
          : button.dataset.sold;
      }
      const price = document.querySelector(
        `[data-price-for="${this.dataset.sectionId}"]`,
      );
      if (price && variant.price_html) {
        const updatedPrice = new DOMParser()
          .parseFromString(variant.price_html, "text/html")
          .querySelector(".price");
        if (updatedPrice) price.innerHTML = updatedPrice.innerHTML;
      }
      this.updateInventory(variant);
      this.updateQuantity(variant);
      this.updateSticky(variant);
      if (variant.featured_media_id) {
        document
          .querySelector("product-gallery")
          ?.selectMedia(String(variant.featured_media_id));
      }
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      history.replaceState({}, "", url);
      announce(
        `Selected ${variant.title}. ${variant.available ? "Available." : "Sold out."}`,
      );
    }

    updateInventory(variant) {
      const inventory = document.querySelector(
        `[data-inventory-for="${this.dataset.sectionId}"]`,
      );
      if (!inventory) return;
      const threshold = Number(inventory.dataset.lowThreshold || 3);
      inventory.classList.toggle("inventory--out", !variant.available);
      if (!variant.available) inventory.textContent = "Currently unavailable";
      else if (
        variant.inventory_management &&
        variant.inventory_policy === "deny" &&
        variant.inventory_quantity > 0 &&
        variant.inventory_quantity <= threshold
      ) {
        inventory.textContent =
          variant.inventory_quantity === 1
            ? "One available"
            : `Only ${variant.inventory_quantity} left`;
      } else inventory.textContent = "Available";
    }

    updateQuantity(variant) {
      const quantity = document
        .getElementById(this.formId)
        ?.querySelector('[name="quantity"]');
      if (!quantity) return;
      if (
        variant.inventory_management &&
        variant.inventory_policy === "deny" &&
        variant.inventory_quantity > 0
      ) {
        quantity.max = variant.inventory_quantity;
      } else quantity.removeAttribute("max");
      if (
        quantity.hasAttribute("max") &&
        Number(quantity.value) > Number(quantity.max)
      ) {
        quantity.value = quantity.max;
      }
    }

    updateSticky(variant) {
      const sticky = document.querySelector("[data-sticky-atc]");
      if (!sticky) return;
      const id = sticky.querySelector("[data-sticky-variant-id]");
      const button = sticky.querySelector('button[type="submit"]');
      const price = sticky.querySelector("[data-sticky-price]");
      if (id && variant) id.value = variant.id;
      if (price && variant?.price_html) price.innerHTML = variant.price_html;
      if (button) {
        button.disabled = !variant?.available;
        button.textContent = variant?.available
          ? button.dataset.add
          : button.dataset.sold;
      }
    }
  }
  customElements.define("variant-selects", VariantSelects);

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.addEventListener("click", (event) => {
        const thumb = event.target.closest("[data-media-id]");
        if (thumb) this.selectMedia(thumb.dataset.mediaId);
      });
    }

    selectMedia(mediaId) {
      this.querySelectorAll("[data-media-id]").forEach((item) =>
        item.setAttribute(
          "aria-current",
          item.dataset.mediaId === mediaId ? "true" : "false",
        ),
      );
      this.querySelectorAll("[data-media-slide]").forEach((slide) =>
        slide.classList.toggle(
          "is-active",
          slide.dataset.mediaSlide === mediaId,
        ),
      );
    }
  }
  customElements.define("product-gallery", ProductGallery);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          fetch(this.dataset.url)
            .then((response) => response.text())
            .then((text) => {
              const content = new DOMParser()
                .parseFromString(text, "text/html")
                .querySelector("product-recommendations");
              if (content?.innerHTML.trim()) this.innerHTML = content.innerHTML;
            });
        },
        { rootMargin: "300px" },
      );
      observer.observe(this);
    }
  }
  customElements.define("product-recommendations", ProductRecommendations);
})();
